export default class FilterTree {
    /**
     * @param {Array<{key: string, children: string[]}>} treeData
     * @param {{container: HTMLElement, categoryLabel?: string}} options
     */
    constructor(treeData, options) {
        this.treeData = treeData;
        this.container = options.container;
        this.categoryLabel = options.categoryLabel || 'Category';
        this.handlers = { change: [] };
        this._eventsBound = false;
        this.selected = new Set();
        this.storageKey = `filterTree‑${window.location.pathname}`;
        this._loadFromSession();
        this.render();
        this._renderApplied();
        if (!this._eventsBound) {
            this.bindEvents();
            this._eventsBound = true;
        }
    }

    update(newTreeData) {
        this.treeData = newTreeData;
        this.render();
        this._renderApplied();
    }

    // Public API: register an event handler
    on(eventName, fn) {
        if (this.handlers[eventName]) {
            this.handlers[eventName].push(fn);
        }
    }

    // Internal: emit an event
    emit(eventName, payload) {
        console.log(`Event emitted: ${eventName}`, payload);
        (this.handlers[eventName] || []).forEach(fn => fn(payload));
    }

    // Build the entire tree UI
    render() {
        this.container.innerHTML = '';
        const fragment = document.createDocumentFragment();

        this.treeData.forEach(node => {
            const section = this.createSection(node);
            fragment.appendChild(section);
        });

        this.container.appendChild(fragment);
    }

    handleizeTag(input) {
        return input
            .toLowerCase() // Convert to lowercase
            .replace(/:/g, "-") // Replace colons with hyphens
            .replace(/\./g, "-") // Replace periods with hyphens
            .replace(/[^a-z0-9-\s]/g, "") // Remove characters that are not alphanumeric, spaces, or hyphens
            .replace(/\s+/g, "-") // Replace spaces with hyphens
            .replace(/-+/g, "-") // Replace multiple hyphens with a single hyphen
            .replace(/^-+|-+$/g, ""); // Remove leading and trailing hyphens
    }

    // Create one collapsible filter section
    // Create one collapsible filter section
    createSection({ key, children }) {
        const wrapper = document.createElement('div');
        wrapper.classList.add('filter-tree__section');

        // — Header
        const header = document.createElement('div');
        header.classList.add('filter-tree__header');
        header.textContent = key;
        header.dataset.key = key;
        wrapper.append(header);

        // — Content (hidden by default)
        const content = document.createElement('div');
        content.classList.add('filter-tree__content');
        content.style.display = 'none';

        if(key === "Series") { // Per Ben's request
            children.sort((a, b) => {
                const order = ['Performance', 'Performance Elite', 'Factory Race'];
                return order.indexOf(a.trim()) - order.indexOf(b.trim());
            });
        }

        // — List of items
        const ul = document.createElement('ul');
        ul.classList.add('filter-tree__list');
        children.forEach((value, idx) => {
            ul.appendChild(this.createItem(key, value, idx));
        });
        content.append(ul);

        // — Apply/Clear controls (hidden by default)
        const actions = document.createElement('div');
        actions.classList.add('filter-tree__actions');
        actions.style.display = 'none';

        const applyBtn = document.createElement('button');
        applyBtn.classList.add('filter-tree__btn', 'filter-tree__btn--apply');
        applyBtn.textContent = 'Apply';

        const clearBtn = document.createElement('button');
        clearBtn.classList.add('filter-tree__btn', 'filter-tree__btn--clear');
        clearBtn.textContent = 'Clear';

        actions.append(clearBtn);

        const panel = document.createElement('div');
        panel.classList.add('filter-tree__panel');
        panel.append(content, actions);
        wrapper.append(panel);

        return wrapper;
    }

    // Create one filter <li><input+label+count></li>
    createItem(type, value, index) {
        const li = document.createElement('li');
        li.classList.add('filter-tree__item');

        // generate a safe ID
        const id = `${this.handleizeTag(type)}-${this.handleizeTag(value)}-${index}`;

        const checkbox = document.createElement('input');
        checkbox.checked = this.selected.has(`${type}:${value}`);
        checkbox.type = 'checkbox';
        checkbox.classList.add('filter-tree__checkbox');
        checkbox.id = id;
        checkbox.name = type;
        checkbox.value = value;

        const label = document.createElement('label');
        label.classList.add('filter-tree__label');
        label.htmlFor = id;
        label.textContent = value.trim();

        // const count = this.counts?.[type]?.[value.trim()] || 0;
        // const countSpan = document.createElement('span');
        // countSpan.classList.add('filter-tree__count');
        // countSpan.textContent = `(${count})`;

        li.append(checkbox, label);
        return li;
    }

    _loadFromSession() {
        try {
            const saved = JSON.parse(sessionStorage.getItem(this.storageKey)) || [];
            saved.forEach(tag => this.selected.add(tag));
        } catch {}
    }

    _saveToSession() {
        sessionStorage.setItem(
            this.storageKey,
            JSON.stringify(Array.from(this.selected))
        );
    }

    _renderApplied() {
        const wrap = this.container.parentElement.querySelector('.filter-tree__applied');
        wrap.innerHTML = '';

        // 1) pills
        this.selected.forEach(tag => {
            const [type, val] = tag.split(':');
            const pill = document.createElement('span');
            pill.classList.add('applied-pill');
            pill.textContent = val;

            const x = document.createElement('button');
            x.innerHTML = '✕';
            x.classList.add('remove-filter-button');
            x.addEventListener('click', () => {
            // 1) update internal state
                this.selected.delete(tag);
                this._saveToSession();

                // 2) uncheck the checkbox in the tree
                const cb = this.container.querySelector(`input[name="${type}"][value="${val}"]`);
                if (cb) cb.checked = false;

                // 3) re‑render pills
                this._renderApplied();

                // 4) emit a proper removal event
                this.emit('change', {
                    type,
                    value: val,
                    checked: false,
                    allSelected: Array.from(this.selected)
                });
            });

            pill.appendChild(x);
            wrap.appendChild(pill);
        });
    }

    _closeAll() {
        this.container
            .querySelectorAll('.filter-tree__section.active')
            .forEach(sec => {
                sec.classList.remove('active');
                sec.querySelector('.filter-tree__content').style.display = 'none';
                sec.querySelector('.filter-tree__actions').style.display = 'none';
            });
    }


    // Attach click and change listeners
    bindEvents() {
        this.container.addEventListener('click', e => {
            const clearBtn = e.target.closest('.filter-tree__btn--clear');
            if (clearBtn) {
                const section = clearBtn.closest('.filter-tree__section');
                const checkboxes = section.querySelectorAll('.filter-tree__checkbox');
                const removals = [];

                checkboxes.forEach(cb => {
                    if (cb.checked) {
                        const tag = `${cb.name}:${cb.value}`;
                        this.selected.delete(tag);
                        removals.push({ type: cb.name, value: cb.value });
                        cb.checked = false;
                    }
                });

                if (removals.length) {
                    this._saveToSession();
                    this._renderApplied();
                    // emit one removal per checkbox so your outer logic can remove them
                    removals.forEach(({type, value}) => {
                        this.emit('change', { type, value, checked: false, allSelected: Array.from(this.selected) });
                    });
                }
                return;
            }

            const header = e.target.closest('.filter-tree__header');
            if (!header) return;

            const panelSection = header.closest('.filter-tree__section');
            const content = panelSection.querySelector('.filter-tree__content');
            const actions = panelSection.querySelector('.filter-tree__actions');
            const isOpen = content.style.display === 'block';

            // 1) Close any other open panels
            this.container
                .querySelectorAll('.filter-tree__section.active')
                .forEach(sec => {
                    if (sec !== panelSection) {
                        sec.classList.remove('active');
                        sec.querySelector('.filter-tree__content').style.display = 'none';
                        sec.querySelector('.filter-tree__actions').style.display = 'none';
                    }
                });

            // 2) Toggle the clicked panel
            if (isOpen) {
                content.style.display = 'none';
                actions.style.display = 'none';
                panelSection.classList.remove('active');
            } else {
                content.style.display = 'block';
                actions.style.display = 'flex';
                panelSection.classList.add('active');
            }
        });

        this.container.addEventListener('change', e => {
            if (!e.target.matches('.filter-tree__checkbox')) return;
            const { name: type, value, checked } = e.target;
            const tag = `${type}:${value}`;
            if (checked) this.selected.add(tag);
            else this.selected.delete(tag);
            this._saveToSession();
            this.emit('change', { type, value, checked, allSelected: Array.from(this.selected) });
            this._renderApplied();
        });

        // Bind Clear All
        const root = this.container.parentElement;
        root.addEventListener('click', e => {
            if (!e.target.matches('.filter-tree__clear-all')) return;

            // capture existing selections
            const toRemove = Array.from(this.selected);

            // clear our UI state
            this.selected.clear();
            this._saveToSession();
            this.container
                .querySelectorAll('.filter-tree__checkbox')
                .forEach(cb => cb.checked = false);
            this._renderApplied();

            // emit a "removal" change for each tag so sidebar.removeFilter() gets a real type/value
            toRemove.forEach(tag => {
                const [type, value] = tag.split(':');
                this.emit('change', { type, value, checked: false, allSelected: [] });
            });
        });

        // close when clicking anywhere outside the filter tree
        document.addEventListener('click', e => {
            // if the click target isn't inside our container or the applied-pill area
            if (!this.container.contains(e.target)
                && !this.container.parentElement
                    .querySelector('.filter-tree__applied')
                    .contains(e.target)
            ) {
                this._closeAll();
            }
        });

    }


}

